// Tender deadlines are either a plain date ("2026-10-08"), which is how the
// submission deadline is entered, or a full ISO timestamp (older manual
// entries, AI values with a clock time, critical dates entered with a time).
// `new Date("2026-10-08")` would read a plain date as UTC midnight, so plain
// dates are always built from their parts in local time here. The old
// date-and-time inputs saved a date picked without a time as UTC midnight
// ("2026-09-09T00:00:00.000Z"); such a stored value is read as that date too.

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/
const UTC_MIDNIGHT = /^(\d{4}-\d{2}-\d{2})T00:00(?::00(?:\.0+)?)?(?:Z|\+00:00)$/

function parseDateOnly(value: string): Date | null {
  const match = DATE_ONLY.exec(value.trim())
  if (!match) return null
  const [, year, month, day] = match
  const date = new Date(Number(year), Number(month) - 1, Number(day))
  // Reject impossible dates such as 2026-02-30, which Date would roll over.
  return date.getMonth() === Number(month) - 1 ? date : null
}

/** The local day a stored value stands for when it carries no real clock time. */
function storedPlainDay(value: string): { iso: string; day: Date } | null {
  const trimmed = value.trim()
  const iso = UTC_MIDNIGHT.exec(trimmed)?.[1] ?? trimmed
  const day = parseDateOnly(iso)
  return day ? { iso, day } : null
}

/** Whether an input value is a plain date ("YYYY-MM-DD"). */
export function isDateOnly(value: string): boolean {
  return parseDateOnly(value) !== null
}

/**
 * A plain date a person can have meant. A date input reports a "complete" date
 * while the year is still being typed (0002, 0020, 0202…); the API rejects
 * years before 2000 for the questions deadline.
 */
export function isPlausibleDate(value: string): boolean {
  return isDateOnly(value) && Number(value.slice(0, 4)) >= 2000
}

/** The moment a deadline passes: a plain date lasts until the end of that day. */
export function deadlineToDate(value: string): Date {
  const plain = storedPlainDay(value)
  if (plain) {
    plain.day.setHours(23, 59, 59, 999)
    return plain.day
  }
  return new Date(value)
}

/**
 * Calendar days from today to the deadline's local day: 0 on the day itself,
 * whether the deadline is a plain date or a timestamp such as 23:59.
 */
export function deadlineDaysLeft(value: string, now: Date = new Date()): number {
  const due = deadlineToDate(value)
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate())
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  // Rounded, because a day across a daylight-saving switch lasts 23 or 25 hours.
  return Math.round((dueDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

/**
 * When the deadline passes, in milliseconds, or null when the value is not a
 * date at all (the AI reports conflicting dates as a note: "Motstridiga
 * uppgifter: …").
 */
export function deadlineTime(value: string | null | undefined): number | null {
  if (!value) return null
  const time = deadlineToDate(value).getTime()
  return Number.isNaN(time) ? null : time
}

/** A plain date shows as a date; a timestamp adds its local time unless `dateOnly`. */
export function formatDeadline(
  value: string | null | undefined,
  locale: string,
  { dateOnly = false }: { dateOnly?: boolean } = {}
): string {
  if (!value) return "—"
  const plain = storedPlainDay(value)
  const parsed = plain?.day ?? new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  const dateFormat = { year: "numeric", month: "short", day: "numeric" } as const
  if (plain || dateOnly) return parsed.toLocaleDateString(locale, dateFormat)
  return parsed.toLocaleString(locale, { ...dateFormat, hour: "2-digit", minute: "2-digit" })
}

const pad = (n: number) => String(n).padStart(2, "0")

/** Value for an `<input type="date">`, in local time. */
export function toDateInputValue(value?: string | null): string {
  if (!value) return ""
  const plain = storedPlainDay(value)
  if (plain) return plain.iso
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ""
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`
}

/** Value for an `<input type="time">`; empty for a plain date. */
export function toTimeInputValue(value?: string | null): string {
  if (!value || storedPlainDay(value)) return ""
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ""
  return `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
}

/**
 * Combine a date input and an optional time input. A date without a time
 * stays a plain date instead of being dropped; with a time it becomes an ISO
 * timestamp. Returns null when there is no valid date.
 */
export function fromDateAndTimeInputs(date: string, time: string): string | null {
  if (!parseDateOnly(date)) return null
  if (!time.trim()) return date.trim()
  const parsed = new Date(`${date.trim()}T${time.trim()}`)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString()
}
